import React, { useCallback, useMemo, memo, useState } from 'react';

import { TFunction } from 'i18next';
import { Lottie } from 'lottie-react';
import { CircleCheckIcon, UserRoundIcon, ZapIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { getIntegrationIcon } from '@/components/organisms/Integrations/icons';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import useMembers from '@/hooks/useMembers';
import useUser from '@/hooks/useUser';
import { useInterval } from '@/hooks/useWindowInterval';
import { cn } from '@/lib/utils';
import { AssigneePopover } from '@/modules/humanChat/components/AssigneePopover/AssigneePopover';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectLast3ViewersBySessionId } from '@/redux/presence/selectors';
import { IntegrationType } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { getChannelKey } from '@/util/channelName';
import { timeFromNowCompact } from '@/util/dateUtils';
import { isKeyEnter, preventClickThrough } from '@/util/util';

import unassignedPulse from './unassigned-pulse.json';

// No preventDefault — it would stop Enter from opening the popover.
const stopPropagation = (e: React.SyntheticEvent) => e.stopPropagation();

const getTriggerIcon = (
  assigneeAgentId: string,
  assigneeBrainId: string,
  assigneeAvatar?: string,
  assigneeName?: string
) => {
  if (assigneeAgentId && !assigneeBrainId) {
    return (
      <Avatar size="xxs">
        <AvatarImage src={assigneeAvatar} alt={assigneeName} />
        <AvatarFallback size="xxs">{assigneeName?.[0]}</AvatarFallback>
      </Avatar>
    );
  }
  if (assigneeBrainId && !assigneeAgentId) {
    return <ZapIcon size={16} />;
  }
  return (
    <span className="relative size-5 text-muted-foreground">
      <Lottie src={unassignedPulse} autoplay className="pointer-events-none absolute inset-0" />
      <UserRoundIcon
        size={13}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
      />
    </span>
  );
};

const getAssigneeTooltip = (
  t: TFunction,
  assigneeAgentId: string,
  assigneeBrainId: string,
  assigneeName?: string
) => {
  if (assigneeAgentId && !assigneeBrainId) {
    return assigneeName ?? '';
  }
  if (assigneeBrainId && !assigneeAgentId) {
    return t('conversation.assistant');
  }
  return t('conversation.unassigned');
};

/**
 * Fallback tile height. SubNavConversation measures each row at runtime
 * (ResizeObserver in MeasuredRow) and reports it back via getItemSize; this value
 * is only the initial estimate used before a row is measured. Keep it equal to the
 * `min-h-[76px]` floor in the JSX below so unmeasured rows reserve the right space.
 */
export const TILE_HEIGHT = 76;

export interface MessagePreviewProps {
  isSelected?: boolean;
  isFirst?: boolean;
  timestamp: string;
  userName: string;
  message?: string | React.ReactNode;
  channel: IntegrationType;
  unreadCount?: number;
  onClick: (sessionId: string) => void;
  sessionId: string;
  assigneeAgentId: string;
  assigneeBrainId: string;
  status: string;
}

const AssigneePopoverButton = memo<{
  sessionId: string;
  assigneeAgentId: string;
  assigneeBrainId: string;
  assigneeAvatar?: string;
  assigneeName?: string;
}>(({ sessionId, assigneeAgentId, assigneeBrainId, assigneeAvatar, assigneeName }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'conversations', actions.WRITE)
  );

  const trigger = useMemo(
    () => (
      <button
        type="button"
        disabled={!canWrite}
        onClick={stopPropagation}
        onKeyDown={stopPropagation}
        className="flex items-center justify-center rounded p-0.5 transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
      >
        {getTriggerIcon(assigneeAgentId, assigneeBrainId, assigneeAvatar, assigneeName)}
      </button>
    ),
    [canWrite, assigneeAgentId, assigneeBrainId, assigneeAvatar, assigneeName]
  );

  return (
    <AssigneePopover
      sessionId={sessionId}
      assigneeAgentId={assigneeAgentId}
      assigneeBrainId={assigneeBrainId}
      open={open}
      onOpenChange={setOpen}
      tooltip={getAssigneeTooltip(t, assigneeAgentId, assigneeBrainId, assigneeName)}
      trigger={trigger}
    />
  );
});

AssigneePopoverButton.displayName = 'AssigneePopoverButton';

// Keep these split: merging them back into the row re-renders the whole tile on every incoming message.
const UserInfo = memo<{ userName: string; message?: string | React.ReactNode }>(
  ({ userName, message }) => (
    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
      <span className="truncate text-sm leading-5 font-normal text-foreground">{userName}</span>
      <span className="line-clamp-2 max-w-full text-sm leading-5 font-normal text-muted-foreground">
        {message}
      </span>
    </div>
  )
);

UserInfo.displayName = 'UserInfo';

const ChannelIcon = memo<{
  channel: IntegrationType;
  Icon?: React.ComponentType<{ size: number }>;
}>(({ channel, Icon }) => {
  const { t } = useTranslation();
  const title = useMemo(() => t(getChannelKey(channel)), [channel, t]);

  if (!Icon) {
    return null;
  }
  return (
    <InfoTooltip content={title} open={title ? undefined : false} showArrow>
      <span className="text-muted-foreground">
        <Icon size={16} />
      </span>
    </InfoTooltip>
  );
});

ChannelIcon.displayName = 'ChannelIcon';

// Owns the tick so a relative-time refresh re-renders this label, not the whole tile.
const TimestampLabel = memo<{ timestamp: string }>(({ timestamp }) => {
  const [tick, setTick] = useState(0);
  useInterval(() => setTick((value) => value + 1), 10_000);

  const formatted = useMemo(
    () => (timestamp ? timeFromNowCompact(timestamp) : ''),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [timestamp, tick]
  );

  if (!formatted) {
    return null;
  }
  return (
    <TimestampTooltip timestamp={timestamp}>
      <span className="inline-flex shrink-0 items-center text-xs leading-5 text-muted-foreground">
        {formatted}
      </span>
    </TimestampTooltip>
  );
});

TimestampLabel.displayName = 'TimestampLabel';

const ViewersRow = memo<{ viewers: { user_id: string }[]; count: number }>(({ viewers, count }) => {
  const { t } = useTranslation();
  const { getMember } = useMembers();

  const tooltip = useMemo(
    () =>
      viewers
        .map(({ user_id }) => getMember(user_id)?.name)
        .filter(Boolean)
        .join(', '),
    [viewers, getMember]
  );

  return (
    <InfoTooltip content={tooltip} side="bottom" align="start">
      <span className="mt-1 block text-xs leading-4 font-semibold text-muted-foreground">
        {t('conversation.viewing', { 0: count, count })}
      </span>
    </InfoTooltip>
  );
});

ViewersRow.displayName = 'ViewersRow';

const MessagePreview = ({
  isSelected = false,
  isFirst = false,
  timestamp,
  userName,
  message = '',
  channel,
  unreadCount = 0,
  onClick,
  sessionId,
  assigneeAgentId,
  assigneeBrainId,
  status,
}: MessagePreviewProps) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { getMember } = useMembers();

  const assigneeUser = getMember(assigneeAgentId);

  const { viewers: otherViewers, count: viewersCount } = useSelector((state: RootState) =>
    selectLast3ViewersBySessionId(state, sessionId, user?.user_id)
  );

  const Icon = getIntegrationIcon(channel);

  const isStatusOpen = status === 'open';

  const handleClick = useCallback(() => onClick(sessionId), [onClick, sessionId]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isKeyEnter(e)) {
        preventClickThrough(e);
        onClick(sessionId);
      }
    },
    [onClick, sessionId]
  );

  return (
    <div
      className={cn(
        'group flex min-h-[76px] w-full cursor-pointer flex-col outline-none',
        !isFirst && 'pt-0.5'
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
    >
      <div
        className={cn(
          'flex flex-1 flex-col rounded border border-transparent p-2 group-focus-visible:ring-2 group-focus-visible:ring-ring group-focus-visible:ring-inset hover:bg-muted',
          isSelected && 'border-border bg-background hover:bg-background'
        )}
      >
        {/* Main row: two columns */}
        <div className="flex gap-2">
          {/* Left column: name + message preview */}
          <UserInfo userName={userName} message={message} />

          {/* Right column: icons + timestamp */}
          <div className="flex shrink-0 flex-col items-end gap-1.5">
            {/* Icons row */}
            <div className="flex items-center gap-1.5">
              {!isStatusOpen && (
                <InfoTooltip content={t('conversation.resolved')} showArrow>
                  <span>
                    <CircleCheckIcon size={12} />
                  </span>
                </InfoTooltip>
              )}

              <AssigneePopoverButton
                sessionId={sessionId}
                assigneeAgentId={assigneeAgentId}
                assigneeBrainId={assigneeBrainId}
                assigneeAvatar={assigneeUser?.avatar}
                assigneeName={assigneeUser?.name}
              />

              <ChannelIcon channel={channel} Icon={Icon} />

              {unreadCount > 0 && (
                <InfoTooltip content={t('conversation.unread')} showArrow>
                  <span className="flex size-5 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
                    {unreadCount}
                  </span>
                </InfoTooltip>
              )}
            </div>

            {/* Timestamp */}
            <TimestampLabel timestamp={timestamp} />
          </div>
        </div>

        {/* The tile already subscribes to members for assigneeUser; this skips a second one. */}
        {viewersCount > 0 && <ViewersRow viewers={otherViewers} count={viewersCount} />}
      </div>
    </div>
  );
};

export default memo(MessagePreview);
